/**
 * CallNotes - Post-call notes interface for agents
 *
 * Features:
 * - Rich text input (≤1000 characters)
 * - Auto-save drafts with debouncing
 * - Timestamp tracking
 * - Keyboard shortcut (Ctrl+N) to focus
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  FileText,
  Save,
  Check,
  Loader2,
  Clock,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';

const MAX_CHARS = 1000;
const AUTOSAVE_DELAY = 2000; // 2 seconds

export interface CallNotesProps {
  /** The call ID to save notes for */
  callId: string;
  /** Initial notes value */
  initialNotes?: string | null;
  /** Last updated timestamp */
  lastUpdated?: string | null;
  /** Whether the component is in a modal/dialog */
  isModal?: boolean;
  /** Callback when notes are saved */
  onSaved?: () => void;
  /** Callback to close modal */
  onClose?: () => void;
}

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export function CallNotes({
  callId,
  initialNotes = '',
  lastUpdated,
  isModal = false,
  onSaved,
  onClose,
}: CallNotesProps) {
  const queryClient = useQueryClient();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const autosaveTimerRef = useRef<NodeJS.Timeout | null>(null);

  const [notes, setNotes] = useState(initialNotes || '');
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [savedAt, setSavedAt] = useState<Date | null>(
    lastUpdated ? new Date(lastUpdated) : null
  );
  const [hasChanges, setHasChanges] = useState(false);

  // Save notes mutation
  const saveMutation = useMutation({
    mutationFn: async (notesText: string) => {
      const { error } = await supabase
        .from('calie_calls')
        .update({
          agent_notes: notesText,
          agent_notes_updated_at: new Date().toISOString(),
        })
        .eq('id', callId);

      if (error) throw error;
      return notesText;
    },
    onSuccess: () => {
      setSaveStatus('saved');
      setSavedAt(new Date());
      setHasChanges(false);
      queryClient.invalidateQueries({ queryKey: ['call', callId] });
      onSaved?.();

      // Reset to idle after showing "saved" briefly
      setTimeout(() => setSaveStatus('idle'), 2000);
    },
    onError: (error) => {
      console.error('[CallNotes] Save error:', error);
      setSaveStatus('error');
      toast.error('Failed to save notes');
    },
  });

  // Handle notes change with autosave
  const handleNotesChange = useCallback((value: string) => {
    // Enforce character limit
    if (value.length > MAX_CHARS) {
      value = value.slice(0, MAX_CHARS);
    }

    setNotes(value);
    setHasChanges(true);
    setSaveStatus('idle');

    // Clear existing autosave timer
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
    }

    // Set new autosave timer
    autosaveTimerRef.current = setTimeout(() => {
      if (value.trim()) {
        setSaveStatus('saving');
        saveMutation.mutate(value);
      }
    }, AUTOSAVE_DELAY);
  }, [saveMutation]);

  // Manual save
  const handleSave = useCallback(() => {
    // Clear autosave timer
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
    }

    setSaveStatus('saving');
    saveMutation.mutate(notes);
  }, [notes, saveMutation]);

  // Keyboard shortcut handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+N to focus notes
      if (e.ctrlKey && e.key === 'n') {
        e.preventDefault();
        textareaRef.current?.focus();
      }

      // Ctrl+S to save (when focused)
      if (e.ctrlKey && e.key === 's' && document.activeElement === textareaRef.current) {
        e.preventDefault();
        handleSave();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleSave]);

  // Cleanup autosave timer on unmount
  useEffect(() => {
    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
      }
    };
  }, []);

  const charsRemaining = MAX_CHARS - notes.length;
  const isNearLimit = charsRemaining <= 100;
  const isAtLimit = charsRemaining <= 0;

  const getSaveStatusDisplay = () => {
    switch (saveStatus) {
      case 'saving':
        return (
          <span className="flex items-center gap-1 text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            Saving...
          </span>
        );
      case 'saved':
        return (
          <span className="flex items-center gap-1 text-green-600">
            <Check className="h-3 w-3" />
            Saved
          </span>
        );
      case 'error':
        return (
          <span className="flex items-center gap-1 text-red-600">
            <AlertTriangle className="h-3 w-3" />
            Error saving
          </span>
        );
      default:
        return hasChanges ? (
          <span className="text-muted-foreground">Unsaved changes</span>
        ) : null;
    }
  };

  const content = (
    <div className="space-y-4">
      {/* Notes Textarea */}
      <div className="space-y-2">
        <Textarea
          ref={textareaRef}
          value={notes}
          onChange={(e) => handleNotesChange(e.target.value)}
          placeholder="Add notes about this call... (Ctrl+N to focus)"
          className="min-h-[150px] resize-none"
          maxLength={MAX_CHARS}
        />

        {/* Character count and status */}
        <div className="flex items-center justify-between text-xs">
          <span className={`${isAtLimit ? 'text-red-600 font-medium' : isNearLimit ? 'text-amber-600' : 'text-muted-foreground'}`}>
            {charsRemaining} characters remaining
          </span>
          {getSaveStatusDisplay()}
        </div>
      </div>

      {/* Last saved timestamp */}
      {savedAt && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Clock className="h-3 w-3" />
          Last saved: {savedAt.toLocaleString()}
        </div>
      )}

      {/* Action buttons */}
      <div className="flex items-center gap-2">
        <Button
          onClick={handleSave}
          disabled={saveStatus === 'saving' || !notes.trim()}
          size="sm"
        >
          {saveStatus === 'saving' ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Notes
            </>
          )}
        </Button>

        {isModal && onClose && (
          <Button variant="outline" size="sm" onClick={onClose}>
            {hasChanges ? 'Save & Close' : 'Close'}
          </Button>
        )}
      </div>

      {/* Keyboard shortcuts hint */}
      <div className="text-xs text-muted-foreground">
        <span className="font-medium">Shortcuts:</span>{' '}
        <kbd className="px-1 py-0.5 bg-muted rounded text-xs">Ctrl+N</kbd> to focus,{' '}
        <kbd className="px-1 py-0.5 bg-muted rounded text-xs">Ctrl+S</kbd> to save
      </div>
    </div>
  );

  // If used as modal, return just the content
  if (isModal) {
    return content;
  }

  // If used as card component
  return (
    <Card>
      <CardHeader className="p-4 sm:p-6">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base sm:text-lg flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Agent Notes
          </CardTitle>
          {notes.trim() && (
            <Badge variant="secondary" className="text-xs">
              {notes.length} / {MAX_CHARS}
            </Badge>
          )}
        </div>
        <CardDescription>
          Add notes about this call for future reference
        </CardDescription>
      </CardHeader>
      <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
        {content}
      </CardContent>
    </Card>
  );
}

/**
 * PostCallNotesModal - Dialog version for post-call workflow
 */
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export interface PostCallNotesModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal should close */
  onOpenChange: (open: boolean) => void;
  /** The call ID */
  callId: string;
  /** Caller information to display */
  callerInfo?: {
    name?: string;
    number?: string;
  };
  /** Call duration in seconds */
  duration?: number;
  /** Callback when notes are saved */
  onSaved?: () => void;
}

export function PostCallNotesModal({
  open,
  onOpenChange,
  callId,
  callerInfo,
  duration,
  onSaved,
}: PostCallNotesModalProps) {
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSaved = () => {
    onSaved?.();
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  const handleSkip = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Call Completed - Add Notes
          </DialogTitle>
          <DialogDescription>
            {callerInfo?.name || callerInfo?.number ? (
              <span>
                Call with: <strong>{callerInfo.name || callerInfo.number}</strong>
                {duration !== undefined && (
                  <span className="ml-2">
                    • Duration: <strong>{formatDuration(duration)}</strong>
                  </span>
                )}
              </span>
            ) : (
              'Add notes about this call for future reference'
            )}
          </DialogDescription>
        </DialogHeader>

        <CallNotes
          callId={callId}
          isModal
          onSaved={handleSaved}
          onClose={handleClose}
        />

        <div className="flex justify-end">
          <Button variant="ghost" size="sm" onClick={handleSkip}>
            Skip
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
